/**
 * Display text for a PRESCRIPTION LINE (MD-232).
 *
 * A line no longer carries a billable therapy — `TherapyRx.therapy` is gone.
 * What it carries is the prescribed TERM plus `rawText`, the verbatim text the
 * doctor wrote in the Heilmittelfeld.
 *
 * Which of the two a surface shows is decided by whether the surface EDITS:
 *
 *  - **Read-only surfaces show the term** (`termText` / `therapyRxTermLabel` /
 *    `therapyRxAmountLabel`). The catalog is the single place an official
 *    Heilmittel wording is maintained, so a rename there has to reach every
 *    screen at once.
 *  - **The editor's therapy cell shows `rawText`** (`therapyRxEditValue`,
 *    `formTherapyFromLine`). It must stay an abbreviation `validateTherapy`
 *    accepts and the server can resolve through the alias table on save.
 *
 * MD-402 is what happens when that line is not drawn. A rawText-first
 * `therapyRxLabel` sat on 15 read-only surfaces — six billing tables among them
 * — so the MD-271 and MD-313 catalog renames could not reach them: `rawText` is
 * written once at save and never revisited, leaving 1.747 lines in MDrelease
 * showing retired codes like "ML3" and "KGN PNF" next to amounts. That helper
 * is gone; do not reintroduce a rawText-first display path.
 *
 * `rawText` itself is deliberately NOT migrated. It is the doctor's original
 * wording and a historical record, the alias table still resolves the old codes
 * on save, and MD-271 and MD-313 both ruled it out of bounds.
 *
 * NB: the performed line item is a per-slot fact — for a slot use
 * `slotTherapy`/`slotTherapyLabel` instead. This is only for the line.
 */
export interface PrescribedTermLike {
  id?: string;
  label?: string;
  shortLabel?: string | null;
  isSupplementary?: boolean;
}

export interface TherapyRxLike {
  rawText?: string | null;
  prescribedTherapy?: PrescribedTermLike | null;
  prescribedTherapyId?: string | null;
}

/**
 * The one term-precedence rule, so that every read-only surface answers the
 * question the same way: the curated shorthand where the catalog has one, the
 * official name otherwise.
 *
 * `rawText` is the last resort rather than an alternative — it is reached only
 * by a line the server could not resolve to a term at all, where the doctor's
 * text is the only thing left to show. Returns "" when a line names nothing;
 * what to show then differs per surface, so each caller ends the chain itself.
 */
export const termText = (line: TherapyRxLike | null | undefined): string =>
  line?.prescribedTherapy?.shortLabel || line?.prescribedTherapy?.label || line?.rawText || "";

/**
 * A prescription line on any read-only surface: cards, chips, tables, the
 * billing tables, dropdown option text.
 *
 * Replaces `therapyRxLabel` (MD-402). Same signature and the same tail of the
 * fallback chain — `rawText`, then the term id — so a line with no resolved
 * term reads exactly as it did before. Only the head changed.
 */
export const therapyRxTermLabel = (line: TherapyRxLike | null | undefined): string =>
  termText(line) || line?.prescribedTherapyId || "";

/**
 * The value the Rx editor's therapy cell binds to — the one place `rawText`
 * belongs. It must stay an ABBREVIATION that `validateTherapy` accepts and that
 * the server resolves text -> alias -> term on save, so it must NOT follow the
 * display helpers to the catalog: a term whose `shortLabel` is not a registered
 * alias would fail the save with RX_THERAPY_MAPPING_FAILED.
 *
 * The visible consequence, accepted: an Rx saved before a catalog rename keeps
 * showing the old code in that cell until someone re-picks the term. Whether to
 * resolve it on load is MD-366's question, not this one's.
 */
export const therapyRxEditValue = (line: TherapyRxLike | null | undefined): string =>
  line?.rawText || line?.prescribedTherapyId || "";

/**
 * A prescribed line for the dense Rx LISTS ("Anzahl Therapien"): "3xKG-ZNS".
 *
 * `amount` is null for open-ended lines (BG Langzeitverordnung, Blanko plan
 * lines) — those show the term alone rather than a made-up "0x".
 */
export interface TherapyRxAmountLike extends TherapyRxLike {
  amount?: number | null;
}

export const therapyRxAmountLabel = (line: TherapyRxAmountLike): string => {
  const term = termText(line) || "-";
  return line.amount ? `${line.amount}x${term}` : term;
};

/** The whole prescribed list of an Rx, in prescription order. */
export const therapyRxAmountList = (
  lines: TherapyRxAmountLike[] | undefined,
  separator = ", "
): string => (lines && lines.length > 0 ? lines.map(therapyRxAmountLabel).join(separator) : "-");

/**
 * The Rx form editors keep their own row shape, whose `therapy` field is an
 * object with `{ id, abbreviation, description, isSupplementary }`. The rest of
 * each form binds to that shape, so rather than rewrite six large forms this
 * adapts an MD-232 API line into it at the boundary.
 *
 * Three inputs arrive here:
 *  - an API line (MD-232): has `prescribedTherapy` / `rawText`, and NO `therapy`
 *  - a form row the user edited: `therapy` is the typed abbreviation STRING
 *  - a form row already adapted: `therapy` is an object with an id
 *
 * `abbreviation` is what the therapy cell shows and what `validateTherapy`
 * checks against the master table, so it must stay an abbreviation — the term's
 * `rawText` — not the human-readable label. Same reasoning as
 * `therapyRxEditValue`; this is the editor side of the MD-402 split.
 */
export interface FormTherapyRow {
  id: string;
  abbreviation: string;
  description: string;
  isSupplementary: boolean;
}

export const formTherapyFromLine = (
  line: any,
  therapyOptions?: any[]
): FormTherapyRow => {
  // already an object carrying an id — leave it alone
  if (line?.therapy && typeof line.therapy === "object" && line.therapy.id) {
    return line.therapy as FormTherapyRow;
  }

  // a form row mid-edit: `therapy` is the typed abbreviation
  if (typeof line?.therapy === "string" && line.therapy) {
    const match = (therapyOptions ?? []).find((t: any) => t?.id === line.therapy);
    return {
      id: line.therapy,
      abbreviation: line.therapy,
      description: match?.description ?? "",
      isSupplementary: line?.isSupplementary === true,
    };
  }

  // an MD-232 API line: the prescribed TERM is the identity
  const term = line?.prescribedTherapy;
  const id = term?.id ?? line?.prescribedTherapyId ?? "";
  return {
    id,
    abbreviation: line?.rawText || id,
    description: term?.label ?? "",
    isSupplementary: term?.isSupplementary === true,
  };
};
